import { Fragment, useEffect, type ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Link,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Heart } from 'lucide-react';
import { Logo } from '../brand/Logo';
import { isAuthenticatedAtom } from '../../store';
import { SITE } from '../../content/site';
import { useSiteConfig, useSupportUrl } from '../../lib/siteConfig';
import { useLanguage, useT, type MessageKey } from '../../i18n';

// The public pages' frame (M42): the logo and Sign in at the top, the policy
// links at the bottom (Razorpay's website check looks for them, D108).
// Their words come from the site catalog (D146), except the policies.

/**
 * Puts React nodes (links, bold text) into a translated sentence's {holes},
 * so each language can order the sentence its own way (D146).
 */
export function richText(
  text: string,
  parts: Record<string, ReactNode>
): ReactNode[] {
  return text.split(/(\{\w+\})/g).map((piece, index) => {
    const name = /^\{(\w+)\}$/.exec(piece)?.[1];
    return (
      <Fragment key={index}>
        {name !== undefined && name in parts ? parts[name] : piece}
      </Fragment>
    );
  });
}

export function ChipInButton({ variant = 'outline' }: { variant?: string }) {
  const supportUrl = useSupportUrl();
  const t = useT();
  if (!supportUrl) return null;
  return (
    <Button
      as="a"
      href={supportUrl}
      target="_blank"
      rel="noopener noreferrer"
      variant={variant}
      colorScheme={variant === 'solid' ? undefined : 'gray'}
      leftIcon={<Heart size={18} />}
      h="52px"
      px={6}
    >
      {t('site.chipIn')}
    </Button>
  );
}

function SiteHeader() {
  const signedIn = useAtomValue(isAuthenticatedAtom);
  const t = useT();
  return (
    <Flex
      as="header"
      align="center"
      justify="space-between"
      maxW="1080px"
      mx="auto"
      px={4}
      pt="max(env(safe-area-inset-top), 16px)"
      pb={4}
    >
      <Link as={RouterLink} to="/" _hover={{ textDecoration: 'none' }}>
        <Logo size={28} />
      </Link>
      <Button
        as={RouterLink}
        to={signedIn ? '/' : '/login'}
        variant="ghost"
        colorScheme="gray"
      >
        {signedIn ? t('site.nav.openTwinden') : t('site.nav.signIn')}
      </Button>
    </Flex>
  );
}

const FOOTER_LINKS: Array<{ to: string; label: MessageKey }> = [
  { to: '/about', label: 'site.nav.about' },
  { to: '/help', label: 'site.nav.help' },
  { to: '/pricing', label: 'site.nav.pricing' },
  // Read-only when signed out (M55, D198).
  { to: '/ideas', label: 'site.nav.ideas' },
  { to: '/privacy', label: 'site.nav.privacy' },
  { to: '/terms', label: 'site.nav.terms' },
  { to: '/contact', label: 'site.nav.contact' },
];

/** The legal pages, which stay in English until a lawyer reviews them. */
const ENGLISH_ONLY_PATHS = ['/privacy', '/terms', '/contact'];

function SiteFooter() {
  const t = useT();
  // Contact Twinden (M58, D209): shown once SUPPORT_EMAIL is set.
  const supportEmail = useSiteConfig()?.supportEmail ?? null;
  return (
    <Box
      as="footer"
      borderTopWidth="1px"
      borderColor="line"
      mt={16}
      pb="max(env(safe-area-inset-bottom), 24px)"
    >
      <Flex
        maxW="1080px"
        mx="auto"
        px={4}
        pt={6}
        gap={4}
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
      >
        <Wrap spacing={5}>
          {FOOTER_LINKS.map((link) => (
            <WrapItem key={link.to}>
              <Link as={RouterLink} to={link.to} color="muted" fontSize="15px">
                {t(link.label)}
              </Link>
            </WrapItem>
          ))}
        </Wrap>
        <Text color="muted" fontSize="14px">
          © {new Date().getFullYear()} {SITE.company} ·{' '}
          <Text as="span" whiteSpace="nowrap">
            {richText(t('site.footer.anotherProject'), {
              company: (
                <Link
                  href={SITE.companyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="ink.body"
                  fontWeight={600}
                >
                  <Image
                    src="/brand/paperdrop-mark.png"
                    alt=""
                    boxSize="18px"
                    display="inline-block"
                    verticalAlign="-4px"
                    mr={1}
                  />
                  {SITE.company}
                </Link>
              ),
            })}
          </Text>
        </Text>
      </Flex>
      {supportEmail && (
        <Box maxW="1080px" mx="auto" px={4} pt={3}>
          <Text color="muted" fontSize="15px">
            {richText(t('site.footer.email'), {
              email: (
                <Link
                  href={`mailto:${supportEmail}`}
                  color="ink.body"
                  fontWeight={600}
                  wordBreak="break-all"
                >
                  {supportEmail}
                </Link>
              ),
            })}
          </Text>
        </Box>
      )}
      {/* Its own line under the row, so the row stays uncrowded (D142). */}
      <Box maxW="1080px" mx="auto" px={4} pt={3}>
        <MadeInIndia />
      </Box>
    </Box>
  );
}

/**
 * A link between the public pages opens the new page at its top, not where
 * the last one was scrolled to; a link to a section (#pricing) goes there.
 */
export function useScrollToTopOnNavigate() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
}

export function SiteShell({ children }: { children: ReactNode }) {
  useScrollToTopOnNavigate();
  const { pathname } = useLocation();
  const language = useLanguage();
  const t = useT();
  return (
    <Box minH="var(--app-height, 100vh)" bg="canvas">
      <SiteHeader />
      {language !== 'en' && ENGLISH_ONLY_PATHS.includes(pathname) && (
        <Box maxW="680px" mx="auto" px={4}>
          <Text
            bg="accent.tint"
            borderRadius="12px"
            px={4}
            py={2}
            fontSize="15px"
            color="ink.body"
          >
            {t('site.policy.englishOnly')}
          </Text>
        </Box>
      )}
      <Box as="main">{children}</Box>
      <SiteFooter />
    </Box>
  );
}

/** A centred column for reading (policies, pricing). */
export function ReadingColumn({ children }: { children: ReactNode }) {
  return (
    <Box maxW="680px" mx="auto" px={4} pt={{ base: 4, md: 10 }}>
      {children}
    </Box>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  // Spacing letters apart would break up Devanagari's joined letters.
  const latin = useLanguage() === 'en';
  return (
    <HStack
      spacing={2}
      color="accent"
      fontSize={latin ? '13px' : '14px'}
      fontWeight={700}
      letterSpacing={latin ? '0.08em' : 0}
      textTransform="uppercase"
    >
      <Text as="span">{children}</Text>
    </HStack>
  );
}

/** The Indian flag, drawn small: three bands and the chakra (D142). */
export function IndiaFlag({ size = 18 }: { size?: number }) {
  const t = useT();
  return (
    <svg
      width={size * 1.5}
      height={size}
      viewBox="0 0 30 20"
      role="img"
      aria-label={t('site.flag.india')}
      style={{
        display: 'inline-block',
        verticalAlign: '-3px',
        borderRadius: 2,
      }}
    >
      <rect width="30" height="20" fill="#FFFFFF" />
      <rect width="30" height="6.67" fill="#FF9933" />
      <rect y="13.33" width="30" height="6.67" fill="#138808" />
      <circle
        cx="15"
        cy="10"
        r="2.6"
        fill="none"
        stroke="#000080"
        strokeWidth="0.8"
      />
      <circle cx="15" cy="10" r="0.6" fill="#000080" />
      <rect
        width="30"
        height="20"
        fill="none"
        stroke="rgba(0,0,0,0.12)"
        strokeWidth="0.5"
      />
    </svg>
  );
}

/** "Made in India" (D142): built in Pune, for families everywhere. */
export function MadeInIndia({ long = false }: { long?: boolean }) {
  const t = useT();
  return (
    <Text as="span" fontSize={long ? '15px' : '14px'} color="muted">
      <IndiaFlag size={long ? 18 : 14} />{' '}
      {long ? (
        <>
          <Text as="span" fontWeight={700} color="ink.body">
            {t('site.madeInIndia.title')}
          </Text>{' '}
          {t('site.madeInIndia.body', { company: SITE.company })}
        </>
      ) : (
        <>{t('site.madeInIndia.footer')}</>
      )}
    </Text>
  );
}
